import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject, ReactNode } from 'react';
import styled from 'styled-components';
import {
  computeScrollPositionForThumbDrag,
  computeScrollPositionForTrackClick,
  computeScrollState,
  computeScrollThumb,
  readScrollMetrics,
  ScrollOrientation,
  SCROLL_INDICATOR_THICKNESS,
} from './scrollMetrics';
import type { ScrollState, ScrollThumb } from './scrollMetrics';

const THUMB_COLOR = '#aeb6ba';
/** Hover/drag highlight. Brighter rather than darker, since this sits on a dark surface. */
const THUMB_COLOR_ACTIVE = '#d2d8db';

/**
 * Fills its parent by default. A scroll container has to be bounded by something, and leaving the
 * height to `auto` means the viewport's own `height: 100%` resolves against nothing -- the content
 * grows instead of scrolling and the indicator never appears. Consumers that size it another way
 * (a flex item, say) override this.
 */
const Outer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
`;

/**
 * Marks the scrolling element. Lets a consumer style the viewport through `styled(ScrollContainer)`
 * (`& > [data-scroll-viewport]`) without depending on child order or on a test id.
 */
export const SCROLL_VIEWPORT_ATTR = 'data-scroll-viewport';

const Viewport = styled.div<{ $orientation: ScrollOrientation; $shift: boolean }>`
  width: 100%;
  height: 100%;

  /* Fixed "scroll" rather than "auto": the native bar is hidden below, and "auto" lets the browser
     re-evaluate overflow as content changes, which reflows the content underneath. */
  ${(props) =>
    props.$orientation === ScrollOrientation.Vertical
      ? 'overflow-y: scroll; overflow-x: hidden;'
      : 'overflow-x: scroll; overflow-y: hidden;'}

  /* Hide the native scrollbar entirely -- the point of this component is that the indicator looks
     identical on macOS (transient overlay bar) and Windows (permanent, space-consuming bar). */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  ${(props) =>
    props.$shift &&
    (props.$orientation === ScrollOrientation.Vertical
      ? `transform: translateX(-${SCROLL_INDICATOR_THICKNESS / 2}px);`
      : `transform: translateY(-${SCROLL_INDICATOR_THICKNESS / 2}px);`)}
`;

/**
 * Takes pointer events so a click on the track can jump the viewport to that position.
 *
 * That does mean this strip swallows clicks aimed at content behind it, where it used to let them
 * through. It is `SCROLL_INDICATOR_THICKNESS` wide and only rendered while the content actually
 * overflows, so the cost is small and the alternative is a track that looks interactive and is not.
 */
const Track = styled.div<{ $orientation: ScrollOrientation }>`
  position: absolute;
  pointer-events: auto;
  cursor: default;

  ${(props) =>
    props.$orientation === ScrollOrientation.Vertical
      ? `top: 0; bottom: 0; right: 0; width: ${SCROLL_INDICATOR_THICKNESS}px;`
      : `left: 0; right: 0; bottom: 0; height: ${SCROLL_INDICATOR_THICKNESS}px;`}
`;

const Thumb = styled.div<{ $orientation: ScrollOrientation }>`
  position: absolute;
  border-radius: ${SCROLL_INDICATOR_THICKNESS / 2}px;
  background-color: ${THUMB_COLOR};

  /* Inherited from the track, stated explicitly: the thumb is the part a pointer interacts with
     most, and a future change to the track's pointer-events should not silently disable it. */
  pointer-events: auto;

  /* Matches a native scrollbar, which keeps the standard arrow over both track and thumb -- it does
     not switch to a pointer or a grab cursor. Stated here rather than left to inherit from the
     track so the thumb cannot pick up a text cursor from surrounding content if the markup moves. */
  cursor: default;

  /* Keep a touch drag on the thumb from being claimed as a page pan/scroll gesture. */
  touch-action: none;

  /* Native scrollbars signal that the thumb is grabbable by highlighting it on hover rather than by
     changing the cursor, and they keep that highlight for the duration of a drag. :active covers
     the drag because the thumb holds pointer capture throughout. */
  transition: background-color 0.12s ease;

  &:hover,
  &:active {
    background-color: ${THUMB_COLOR_ACTIVE};
  }

  ${(props) =>
    props.$orientation === ScrollOrientation.Vertical
      ? 'right: 0; width: 100%;'
      : 'bottom: 0; height: 100%;'}
`;

function isSameThumb(a: ScrollThumb | null, b: ScrollThumb | null): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return a.offset === b.offset && a.size === b.size;
}

function isSameState(a: ScrollState | null, b: ScrollState): boolean {
  return (
    a != null &&
    a.isScrollable === b.isScrollable &&
    a.canScrollBackward === b.canScrollBackward &&
    a.canScrollForward === b.canScrollForward
  );
}

export interface ScrollContainerProps {
  children: ReactNode;
  /** Axis the content scrolls along. Defaults to horizontal. */
  orientation?: ScrollOrientation;
  /**
   * Shift the content half the indicator's thickness away from the indicator while it is visible,
   * so a scrollable container reads as slightly taller (or wider) rather than letting the indicator
   * sit on top of the content. Off by default: the indicator overlays the content, which is what
   * most containers want.
   */
  shiftContentWhenVisible?: boolean;
  /** Notified when scrollability changes. Drives external affordances such as arrow buttons. */
  onScrollStateChange?: (state: ScrollState) => void;
  /**
   * Populated with the scrolling element, for imperative reads and writes (`scrollLeft`, `scrollBy`,
   * descendant queries). The children render as its direct descendants, so `firstElementChild` is
   * the consumer's own content root.
   */
  viewportRef?: MutableRefObject<HTMLDivElement | null>;
  /** Applied to the scrolling viewport, so consumers can lay out its children. */
  viewportClassName?: string;
  /** Applied to the outer positioned wrapper. Enables `styled(ScrollContainer)`. */
  className?: string;
  'data-testid'?: string;
}

/**
 * A scrolling container with a custom scroll indicator in place of the native scrollbar.
 *
 * Exists because the native bar does not render consistently across platforms -- macOS draws a
 * transient overlay while Windows draws a permanent bar that consumes layout space, so identical
 * content ends up at different sizes on the two. This hides the native bar and draws its own
 * indicator, which auto-hides whenever the content fits.
 *
 * The indicator behaves like a native scrollbar: the thumb can be dragged, and clicking the track
 * jumps the viewport so the thumb centres on the click. Scrolling also works through the wheel,
 * trackpad, keyboard, or whatever affordance the consumer supplies.
 */
export function ScrollContainer({
  children,
  orientation = ScrollOrientation.Horizontal,
  shiftContentWhenVisible = false,
  onScrollStateChange,
  viewportRef,
  viewportClassName,
  className,
  'data-testid': dataTestId,
}: ScrollContainerProps): React.ReactElement {
  const internalRef = useRef<HTMLDivElement | null>(null);
  const [thumb, setThumb] = useState<ScrollThumb | null>(null);

  // Held in refs so `update` stays referentially stable. It backs a ResizeObserver and a scroll
  // handler, and it runs on every render (see the effect below) -- so it must not be rebuilt each
  // time, and it must not fire a state update unless something actually moved, or the
  // render -> update -> setState -> render cycle would never settle.
  const onScrollStateChangeRef = useRef(onScrollStateChange);
  onScrollStateChangeRef.current = onScrollStateChange;
  const lastStateRef = useRef<ScrollState | null>(null);

  const setViewport = useCallback(
    (node: HTMLDivElement | null) => {
      internalRef.current = node;
      if (viewportRef) viewportRef.current = node;
    },
    [viewportRef]
  );

  const update = useCallback(() => {
    const element = internalRef.current;
    if (!element) return;

    const metrics = readScrollMetrics(element, orientation);

    const nextThumb = computeScrollThumb(metrics);
    setThumb((prev) => (isSameThumb(prev, nextThumb) ? prev : nextThumb));

    const nextState = computeScrollState(metrics);
    if (!isSameState(lastStateRef.current, nextState)) {
      lastStateRef.current = nextState;
      onScrollStateChangeRef.current?.(nextState);
    }
  }, [orientation]);

  /**
   * Pointer position and scroll offset captured when a thumb drag began, or null when no drag is in
   * progress. A ref rather than state: it changes on every pointer move and nothing renders from it.
   */
  const dragRef = useRef<{ pointerStart: number; scrollStart: number } | null>(null);

  const pointerPosition = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) =>
      orientation === ScrollOrientation.Vertical ? event.clientY : event.clientX,
    [orientation]
  );

  const handleThumbPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const element = internalRef.current;
      if (!element) return;

      // Left button only, matching a native scrollbar; a right-click should open a context menu
      // rather than start a drag.
      if (event.button !== 0) return;

      // Stops the press from also selecting text or starting a native image/element drag.
      event.preventDefault();

      dragRef.current = {
        pointerStart: pointerPosition(event),
        scrollStart:
          orientation === ScrollOrientation.Vertical ? element.scrollTop : element.scrollLeft,
      };

      // Route every later pointer event for this gesture to the thumb. Without it the drag dies the
      // moment the pointer leaves a track that is only SCROLL_INDICATOR_THICKNESS px wide -- which
      // is immediately, since dragging along the axis means sliding across that narrow strip.
      // Guarded because jsdom does not implement pointer capture.
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [orientation, pointerPosition]
  );

  const handleThumbPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      const element = internalRef.current;
      if (drag == null || !element) return;

      const nextPosition = computeScrollPositionForThumbDrag({
        metrics: readScrollMetrics(element, orientation),
        pointerDelta: pointerPosition(event) - drag.pointerStart,
        scrollPositionAtDragStart: drag.scrollStart,
      });
      if (nextPosition == null) return;

      if (orientation === ScrollOrientation.Vertical) {
        element.scrollTop = nextPosition;
      } else {
        element.scrollLeft = nextPosition;
      }
    },
    [orientation, pointerPosition]
  );

  const handleThumbPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  /**
   * Jump to a clicked point on the track.
   *
   * Clicks that land on the thumb are ignored -- the thumb is a child of the track, so they arrive
   * here too, and treating one as a jump would shift the view out from under a pointer that was
   * aimed at the thumb itself.
   *
   * Bound to pointerdown rather than click, matching a native scrollbar, which acts on press. The
   * whole component speaks pointer events so the thumb's drag and this share one model.
   */
  const handleTrackPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) return;
      if (event.button !== 0) return;

      const element = internalRef.current;
      if (!element) return;

      const rect = event.currentTarget.getBoundingClientRect();
      const trackPosition =
        orientation === ScrollOrientation.Vertical
          ? event.clientY - rect.top
          : event.clientX - rect.left;

      const nextPosition = computeScrollPositionForTrackClick({
        metrics: readScrollMetrics(element, orientation),
        trackPosition,
      });
      if (nextPosition == null) return;

      // The viewport's own scroll handler runs `update`, which moves the thumb.
      if (orientation === ScrollOrientation.Vertical) {
        element.scrollTop = nextPosition;
      } else {
        element.scrollLeft = nextPosition;
      }
    },
    [orientation]
  );

  // Runs on mount and after every render. Content can change size without the viewport resizing and
  // without any scroll event -- a chip is added, a font finishes loading -- and `children` changing
  // is the one signal that covers all of those. The equality guards in `update` keep this from
  // looping.
  useEffect(update);

  // Catches the other direction: the viewport itself resizing while the content is unchanged, e.g.
  // the step bar being drag-resized.
  useEffect(() => {
    const element = internalRef.current;
    if (!element) return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [update]);

  return (
    <Outer className={className} data-testid={dataTestId}>
      <Viewport
        ref={setViewport}
        className={viewportClassName}
        {...{ [SCROLL_VIEWPORT_ATTR]: '' }}
        onScroll={update}
        $orientation={orientation}
        $shift={shiftContentWhenVisible && thumb != null}
        data-testid={dataTestId != null ? `${dataTestId}-viewport` : undefined}
      >
        {children}
      </Viewport>
      {thumb != null && (
        <Track
          $orientation={orientation}
          onPointerDown={handleTrackPointerDown}
          data-testid="scroll-indicator-track"
        >
          <Thumb
            $orientation={orientation}
            onPointerDown={handleThumbPointerDown}
            onPointerMove={handleThumbPointerMove}
            onPointerUp={handleThumbPointerUp}
            onPointerCancel={handleThumbPointerUp}
            data-testid="scroll-indicator-thumb"
            style={
              orientation === ScrollOrientation.Vertical
                ? { top: thumb.offset, height: thumb.size }
                : { left: thumb.offset, width: thumb.size }
            }
          />
        </Track>
      )}
    </Outer>
  );
}
